import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import { cn } from '@ludus/ui'
import type { PipelineId } from '@ludus/core'
import { fetchFlowRun } from '@/lib/api/flows.ts'
import { nodeTypeKey } from '@/features/flows/Canvas/useNodeLabels.ts'
import { isKnownNodeType, type FlowGraph, type FlowRunStep } from '@/features/flows/types.ts'

/**
 * One run's step log, joined to the graph it can still be read against.
 *
 * **`nodeId` first, `key` second** (D12, and Directus's log drawer does the
 * same). A step cites the node it ran; if that node is still in the graph, the
 * step is named by what the node *is* now. If it has been deleted, the stored
 * `key` is what the log has left — which is exactly why `deleteNode` retires a
 * key rather than freeing it, so a step's name can never resolve to a *different*
 * node minted later with the same one.
 *
 * The run is fetched by id and not derived from the list, because the list row
 * carries no `steps[]`: the catalogue route omits them by design, and inventing
 * them from the summary would be showing a log that was never read.
 */

interface RunDetailProps {
  pipelineId: PipelineId
  flowId: string
  runId: string
  graph: FlowGraph
  onTouchedNodes: (nodeIds: readonly string[]) => void
}

export function RunDetail({ pipelineId, flowId, runId, graph, onTouchedNodes }: RunDetailProps) {
  const { t } = useTranslation()
  const swr = useSWR(`/pipelines/${pipelineId}/flows/${flowId}/runs/${runId}`, () =>
    fetchFlowRun(pipelineId, flowId, runId),
  )

  const steps = swr.data?.steps
  useEffect(() => {
    onTouchedNodes(steps === undefined ? [] : steps.map((s) => s.nodeId))
    // Cleared on unmount so closing the detail also clears the canvas highlight;
    // the panel above owns which run is open, and this owns what it touched.
    return () => onTouchedNodes([])
  }, [steps, onTouchedNodes])

  if (swr.isLoading) {
    return (
      <p className="px-3 py-2 text-[11px] text-muted">
        {t('pages.flows.runs.loadingSteps', { defaultValue: 'Loading steps…' })}
      </p>
    )
  }

  if (swr.error !== undefined || swr.data === undefined) {
    return (
      <p className="px-3 py-2 text-[11px] text-toast-error-text">
        {t('pages.flows.runs.stepsError', {
          defaultValue: 'Could not load this run’s steps.',
        })}
      </p>
    )
  }

  if (swr.data.steps.length === 0) {
    return (
      <p className="px-3 py-2 text-[11px] italic text-muted">
        {t('pages.flows.runs.noSteps', { defaultValue: 'This run recorded no steps.' })}
      </p>
    )
  }

  return (
    <ol className="flex flex-col gap-1.5 border-b border-border/50 bg-background/40 px-3 py-2">
      {swr.data.steps.map((step, i) => (
        <StepRow key={`${step.nodeId}-${i}`} step={step} graph={graph} />
      ))}
    </ol>
  )
}

function StepRow({ step, graph }: { step: FlowRunStep; graph: FlowGraph }) {
  const { t } = useTranslation()
  const node = graph.nodes.find((n) => n.id === step.nodeId) ?? null

  const name =
    node !== null && isKnownNodeType(node.type)
      ? t(`pages.flows.canvas.type.${nodeTypeKey(node.type)}`, { defaultValue: node.type })
      : // Gone from the graph: the stored key is what the log has, and it is
        // never reused because `deleteNode` retires it.
        t('pages.flows.runs.deletedNode', {
          key: step.key,
          defaultValue: '{{key}} (removed)',
        })

  return (
    <li className="flex flex-col gap-0.5">
      <div className="flex items-center gap-2">
        <span
          className={cn(
            'h-1.5 w-1.5 shrink-0 rounded-full',
            step.status === 'done'
              ? 'bg-toast-success-text'
              : step.status === 'skipped'
                ? 'bg-muted'
                : 'bg-toast-error-text',
          )}
          aria-hidden="true"
        />
        <span className="truncate text-[11px] text-foreground">{name}</span>
        <span className="ml-auto shrink-0 font-mono text-[10px] text-muted">{step.key}</span>
      </div>

      <span className="pl-3.5 text-[10px] text-muted">
        {t(`pages.flows.runs.step.${step.status}`, { defaultValue: step.status })}
        {step.reason !== undefined && ` · ${step.reason}`}
      </span>

      {/* The options *as rendered* — what the templates actually became on this
          run, which is the question a step log exists to answer. */}
      {Object.keys(step.renderedOptions).length > 0 && (
        <dl className="flex flex-col gap-0.5 pl-3.5">
          {Object.entries(step.renderedOptions).map(([option, value]) => (
            <div key={option} className="flex min-w-0 items-baseline gap-1.5">
              <dt className="shrink-0 text-[10px] uppercase text-muted">
                {t(`pages.flows.canvas.option.${option}`, { defaultValue: option })}
              </dt>
              <dd className="min-w-0 flex-1 truncate text-right font-mono text-[10px] text-foreground/80">
                {typeof value === 'string' ? value : JSON.stringify(value)}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </li>
  )
}
